import type { PluginClientManifest } from "./types";

// 09b-01 (D-01 + D-05): the four remaining core→plugin route couplings are
// severed by relocating their wiring INTO the owning plugin's client manifest.
// Authenticated tenant/admin routes (/settings/billing, /settings/privacy,
// /admin/plugins/sample-notes) live in `routes[]` and mount under
// `authenticatedRoute` via `buildPluginRoutes`. The one public, session-less
// route (/pricing) lives in the billing manifest's `publicRoutes[]` and mounts
// under `rootRoute` via `buildPluginPublicRoutes`. Because fork:init's
// plugin-strip removes the whole manifest object, every relocated route (and its
// nav item, already flag-gated in useNavSections) disappears with the plugin —
// leaving zero dangling core→plugin import for `tsc -b` to trip on.

// Plugin i18n bundles. Registered at startup via loadPluginI18nNamespaces so
// plugin screens resolve keys instead of rendering raw key paths.
import enNotes from "../../../../plugins/sample-notes/src/client/locales/en/notes.json";
import esNotes from "../../../../plugins/sample-notes/src/client/locales/es/notes.json";
import frNotes from "../../../../plugins/sample-notes/src/client/locales/fr/notes.json";
import enBilling from "../../../../plugins/billing/src/client/locales/en/billing.json";
import esBilling from "../../../../plugins/billing/src/client/locales/es/billing.json";
import frBilling from "../../../../plugins/billing/src/client/locales/fr/billing.json";
import enPrivacy from "../../../../plugins/privacy/src/client/locales/en/privacy.json";
import esPrivacy from "../../../../plugins/privacy/src/client/locales/es/privacy.json";
import frPrivacy from "../../../../plugins/privacy/src/client/locales/fr/privacy.json";
import enAnalytics from "../../../../plugins/analytics/src/client/locales/en/analytics.json";
import esAnalytics from "../../../../plugins/analytics/src/client/locales/es/analytics.json";
import frAnalytics from "../../../../plugins/analytics/src/client/locales/fr/analytics.json";
import enNotifications from "../../../../plugins/notifications/src/client/locales/en/notifications.json";
import esNotifications from "../../../../plugins/notifications/src/client/locales/es/notifications.json";
import frNotifications from "../../../../plugins/notifications/src/client/locales/fr/notifications.json";
import enSiteContent from "../../../../plugins/site-content/src/client/locales/en/site-content.json";
// site-content owns its OWN copy of the analytics-namespace labels its
// marketing-analytics sub-view reads (09b-03, Defect 1): keeping these inside the
// site-content plugin dir means an `analytics` plugin strip no longer leaves
// site-content referencing deleted plugins/analytics locale JSON. Only the subset
// SiteAnalyticsContainer actually reads (a11y + range) is copied.
import enScAnalytics from "../../../../plugins/site-content/src/client/locales/en/analytics.json";
import esScAnalytics from "../../../../plugins/site-content/src/client/locales/es/analytics.json";
import frScAnalytics from "../../../../plugins/site-content/src/client/locales/fr/analytics.json";
import enReference from "../../../../plugins/reference-app/src/client/locales/en/reference-app.json";
import esReference from "../../../../plugins/reference-app/src/client/locales/es/reference-app.json";
import frReference from "../../../../plugins/reference-app/src/client/locales/fr/reference-app.json";

/**
 * Static client-side plugin manifest registry.
 * Mirrors the backend's _registry.generated.ts pattern.
 *
 * Each plugin that has frontend surface area registers its client manifest here.
 * This file is manually maintained until a build-time generation script is added.
 */
export const clientPluginManifests: PluginClientManifest[] = [
  {
    id: "sample-notes",
    name: "Notes",
    version: "2026.2.1",
    description: "Personal notes with color labels, pinning, and soft delete",
    featureFlagKey: "plugin.sample-notes.enabled",
    capabilities: ["database", "cache", "events"],
    navigation: {
      section: "plugins",
      position: 100,
      items: [{ labelKey: "nav.title", href: "/notes", icon: "StickyNote" }],
    },
    routes: [
      {
        path: "/notes",
        component: () =>
          import("../../../../plugins/sample-notes/src/client/containers/NotesListContainer"),
      },
      {
        path: "/notes/trash",
        component: () =>
          import("../../../../plugins/sample-notes/src/client/containers/NotesTrashContainer"),
      },
      {
        path: "/notes/$noteId",
        component: () =>
          import("../../../../plugins/sample-notes/src/client/containers/NoteDetailContainer"),
      },
      // Admin settings surface (09b-01, D-05): relocated from the hard-coded
      // adminPluginSettingsRoute in router.tsx into routes[] so it strips with the
      // plugin. Mounts under authenticatedRoute via buildPluginRoutes; the
      // NotesSettingsContainer's tRPC calls stay admin-scoped server-side
      // (adminProcedure), which is the authoritative RBAC gate (T-09b-01).
      {
        path: "/admin/plugins/sample-notes",
        // Any-admin (tenant admin or super admin), mirroring the guard the former
        // adminLayoutRoute carried. Defense-in-depth for the adminProcedure gate.
        requiresAdmin: true,
        component: () =>
          import("../../../../plugins/sample-notes/src/client/containers/NotesSettingsContainer"),
      },
    ],
    i18nNamespaces: ["notes"],
    i18nResources: {
      en: { notes: enNotes },
      es: { notes: esNotes },
      fr: { notes: frNotes },
    },
  },
  {
    id: "billing",
    name: "Billing",
    version: "2026.6.1",
    description: "Stripe-backed subscriptions, plans, invoices, and the billing webhook",
    featureFlagKey: "billing",
    capabilities: ["database", "events", "email"],
    // No `navigation` here: the tenant "Billing" nav item stays a CORE settings
    // item gated on the billing flag in buildNavSections (the chosen mechanism
    // — adding it here too would duplicate it). The ROUTE wiring, however, now
    // lives in the manifest (09b-01, D-01/D-05): the tenant /settings/billing
    // route + the super-admin /admin/billing overview mount under
    // authenticatedRoute via buildPluginRoutes; the public /pricing catalog
    // mounts session-less under rootRoute via buildPluginPublicRoutes. Registering
    // billing also adds its flag to the fetched plugin flag set.
    routes: [
      {
        path: "/admin/billing",
        // Super-admin only (SuperAdminBillingContainer / superAdminProcedure).
        // Defense-in-depth mirror of the former adminLayoutRoute + child guard.
        requiresSuperAdmin: true,
        component: () =>
          import("../../../../plugins/billing/src/client/containers/SuperAdminBillingContainer"),
      },
      {
        path: "/settings/billing",
        // Tenant-admin only (BillingContainer / adminProcedure). Client mirror
        // of the server RBAC so non-admins are redirected, not shown FORBIDDEN.
        requiresAdmin: true,
        component: () =>
          import("../../../../plugins/billing/src/client/containers/BillingContainer"),
      },
    ],
    // Public, session-less route (09b-01, D-05): /pricing lets unauthenticated
    // visitors browse the plan catalog and start checkout. Mounted under rootRoute
    // by buildPluginPublicRoutes (NOT authenticatedRoute — that would bounce
    // visitors to /login). Strips cleanly when the billing manifest is removed.
    publicRoutes: [
      {
        path: "/pricing",
        component: () =>
          import("../../../../plugins/billing/src/client/containers/PricingContainer"),
      },
    ],
    i18nNamespaces: ["billing"],
    i18nResources: {
      en: { billing: enBilling },
      es: { billing: esBilling },
      fr: { billing: frBilling },
    },
  },
  {
    id: "privacy",
    name: "Privacy",
    version: "2026.6.1",
    description: "GDPR consent, data export, right-to-deletion, and per-tenant privacy policy",
    featureFlagKey: "privacy",
    capabilities: ["database", "events", "email", "storage"],
    // No `navigation` here: the tenant "Privacy" nav item stays a CORE settings
    // item gated on the privacy flag in buildNavSections (mirrors billing —
    // adding it here too would duplicate it). The ROUTE wiring now lives in the
    // manifest (09b-01, D-01/D-05): /settings/privacy renders the drop-in
    // ConsentSettingsContainer (the complete consent/export/deletion surface).
    //
    // The in-app privacy page (/privacy, Surface 3) and the tenant-admin policy
    // editor (/admin/privacy, Surface 4) are registered here now that their
    // components exist (06-09 Task 1) — picked up by buildPluginRoutes under the
    // authenticated route. policy.update is server-gated to TENANT_ADMIN; the
    // FeatureGate inside each container handles runtime flag gating. Registering
    // the privacy manifest also adds its flag to the fetched plugin flag set.
    routes: [
      {
        path: "/privacy",
        component: () =>
          import("../../../../plugins/privacy/src/client/containers/PrivacyPolicyContainer"),
      },
      {
        path: "/admin/privacy",
        // Any-admin: policy.update is server-gated to TENANT_ADMIN. Defense-in-depth
        // mirror of the former adminLayoutRoute guard.
        requiresAdmin: true,
        component: () =>
          import("../../../../plugins/privacy/src/client/containers/PolicyEditorContainer"),
      },
      {
        path: "/settings/privacy",
        component: () =>
          import("../../../../plugins/privacy/src/client/containers/ConsentSettingsContainer"),
      },
    ],
    // First-visit consent banner (Surface 1): app-wide chrome mounted by the
    // shell's globalComponents seam, flag-gated there. The container hides
    // itself once consent.get reports a recorded decision.
    globalComponents: [
      {
        id: "consent-banner",
        component: () =>
          import("../../../../plugins/privacy/src/client/containers/ConsentBannerContainer"),
      },
    ],
    i18nNamespaces: ["privacy"],
    i18nResources: {
      en: { privacy: enPrivacy },
      es: { privacy: esPrivacy },
      fr: { privacy: frPrivacy },
    },
  },
  {
    id: "analytics",
    name: "Analytics",
    version: "2026.7.1",
    description: "Platform + tenant analytics dashboards over Workers Analytics Engine",
    featureFlagKey: "analytics",
    capabilities: ["cache"],
    // No `navigation` here: the analytics nav items are RBAC-gated CORE admin
    // items added in buildNavSections (useNavSections) — platform analytics is
    // super-admin only, tenant analytics is any-admin. Declaring navigation here
    // too would duplicate them and would not carry the RBAC gating. The two
    // dashboard routes are registered below and picked up by buildPluginRoutes
    // under the authenticated route; each container's FeatureGate handles the
    // runtime flag gating. Registering the manifest also adds "analytics" to the
    // fetched plugin flag set.
    routes: [
      {
        path: "/admin/analytics",
        // Platform analytics is super-admin only. Defense-in-depth mirror of the
        // RBAC-gated core nav item + server superAdminProcedure.
        requiresSuperAdmin: true,
        component: () =>
          import("../../../../plugins/analytics/src/client/PlatformAnalyticsContainer"),
      },
      {
        path: "/admin/tenant-analytics",
        // Tenant analytics is any-admin. Defense-in-depth mirror of the former
        // adminLayoutRoute guard.
        requiresAdmin: true,
        component: () =>
          import("../../../../plugins/analytics/src/client/TenantAnalyticsContainer"),
      },
    ],
    i18nNamespaces: ["analytics"],
    i18nResources: {
      en: { analytics: enAnalytics },
      es: { analytics: esAnalytics },
      fr: { analytics: frAnalytics },
    },
  },
  {
    id: "notifications",
    name: "Notifications",
    version: "2026.7.1",
    description: "In-app notification center, unread bell, and per-category preferences",
    featureFlagKey: "notifications",
    capabilities: ["database", "events", "email"],
    // The TopBar bell mounts via the CORE `topbarActions` slot (review HIGH #3) —
    // the core TopBar renders it generically and never imports this plugin. The
    // `buildTopbarActions` builder gates it on the "notifications" flag.
    topbarActions: [
      {
        id: "notification-bell",
        component: () => import("../../../../plugins/notifications/src/client/NotificationBell"),
      },
    ],
    // The notification preferences panel (NOTF, D-16) is a CORE settings route
    // (the /settings/notifications nav item is added in useNavSections). The
    // container's FeatureGate handles runtime flag gating; registering the
    // manifest also adds "notifications" to the fetched plugin flag set.
    routes: [
      {
        path: "/settings/notifications",
        component: () =>
          import("../../../../plugins/notifications/src/client/NotificationPreferencesContainer"),
      },
    ],
    i18nNamespaces: ["notifications"],
    i18nResources: {
      en: { notifications: enNotifications },
      es: { notifications: esNotifications },
      fr: { notifications: frNotifications },
    },
  },
  {
    id: "site-content",
    name: "Site",
    version: "2026.8.1",
    description: "Super-admin marketing-site content editor (Lexical, draft→publish, R2 media)",
    featureFlagKey: "site-content",
    capabilities: ["database", "cache", "storage"],
    // No `navigation` here: the super-admin "Site" nav item is an RBAC-gated CORE
    // admin item added in buildNavSections (useNavSections) — it is super-admin
    // only, mirroring how platform analytics is gated. Declaring navigation here
    // too would duplicate it and would not carry the RBAC gating. The authoritative
    // gate is the server `superAdminProcedure` (08-03, D-08/D-17); the nav gate +
    // the container's FeatureGate are defense-in-depth UX only.
    //
    // The editor route + the marketing-analytics sub-view route are registered
    // below and picked up by buildPluginRoutes under the authenticated route. Both
    // live under the super-admin "Site" area (08-06 editor + 08-07 analytics). The
    // analytics sub-view REUSES the Phase 7 dashboard components, which read the
    // "analytics" i18n namespace for their chart/a11y labels — so this manifest
    // declares BOTH the "site-content" and "analytics" namespaces. Registering the
    // manifest adds "site-content" to the fetched plugin flag set.
    routes: [
      {
        path: "/admin/site",
        // Super-admin only (server superAdminProcedure, 08-03/D-08/D-17).
        // Defense-in-depth client redirect (WR-01).
        requiresSuperAdmin: true,
        component: () =>
          import("../../../../plugins/site-content/src/client/SiteContentEditorContainer"),
      },
      {
        // Marketing-analytics dashboard sub-view (SITE-13, D-32). Super-admin only —
        // the authoritative gate is the server `superAdminProcedure` (08-07); the
        // nav gate + the container's FeatureGate are defense-in-depth UX only.
        path: "/admin/site/analytics",
        requiresSuperAdmin: true,
        component: () =>
          import("../../../../plugins/site-content/src/client/SiteAnalyticsContainer"),
      },
    ],
    i18nNamespaces: ["site-content", "analytics"],
    i18nResources: {
      // analytics-namespace labels come from site-content's OWN copy (Defect 1) so
      // they survive an analytics-plugin strip; the analytics plugin keeps its own.
      en: { "site-content": enSiteContent, analytics: enScAnalytics },
      es: { analytics: esScAnalytics },
      fr: { analytics: frScAnalytics },
    },
  },
  {
    id: "reference-app",
    name: "Reference",
    version: "2026.2.1",
    description:
      "Canonical reference plugin: projects→tasks relations, StorageClient uploads, admin-vs-user RBAC",
    featureFlagKey: "plugin.reference-app.enabled",
    capabilities: ["database", "storage", "events"],
    navigation: {
      section: "plugins",
      position: 110,
      items: [{ labelKey: "nav.title", href: "/reference", icon: "FolderKanban" }],
    },
    routes: [
      {
        path: "/reference",
        component: () =>
          import("../../../../plugins/reference-app/src/client/containers/ProjectsListContainer"),
      },
      {
        path: "/reference/$projectId",
        component: () =>
          import("../../../../plugins/reference-app/src/client/containers/ProjectDetailContainer"),
      },
    ],
    i18nNamespaces: ["reference-app"],
    i18nResources: {
      en: { "reference-app": enReference },
      es: { "reference-app": esReference },
      fr: { "reference-app": frReference },
    },
  },
];
